import { lazy, Suspense, useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { getChangeVersion, notifyChanges, subscribeChanges } from "../changes.ts";
import {
  api, type ActionCard, type MyStats, type ScheduleData, type ScheduleItem,
} from "../api.ts";
import { ActionCardView } from "../components/ActionCardView.tsx";
import { EditScheduleButton, ScheduleEditor } from "../components/ScheduleEditor.tsx";
import { fmtMoney } from "../labels.ts";
import { goCustomer, goCustomers, goKnowledge } from "../navigate.ts";
import { EmptyState, ErrorState, Icon, LoadingState } from "../components/Workspace.tsx";
import "./today.css";

const WorkCalendar = lazy(() => import("../components/WorkCalendar.tsx").then((module) => ({ default: module.WorkCalendar })));

/**
 * 行动导航(原型 P1)。**销售打开系统看到的第一屏。**
 *
 * 四层,顺序是北极星定的「固定信息在上、变长瀑布流在下」:
 *   ① 问候 + 今天是哪天
 *   ② 我的客户分布 / 业绩统计 —— 两张并排的固定卡
 *   ③ 日程安排 —— 本周(上午/下午 × 周一至周日)与本月两个视图
 *   ④ 今日行动 —— 三引擎产出的动作卡,按赢单影响排序
 *
 * 判别表第 5 行:打开系统是空白等你,还是已经告诉你该干嘛。所以这一屏
 * 不是列表也不是看板,是**已经排好序的几件事**。
 */
export function Today(props: { userName: string; canViewCustomers?: boolean }): React.JSX.Element {
  const version = useSyncExternalStore(subscribeChanges, getChangeVersion, () => 0);
  const [cards, setCards] = useState<ActionCard[]>([]);
  const [stats, setStats] = useState<MyStats | undefined>(undefined);
  const [sched, setSched] = useState<ScheduleData | undefined>(undefined);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [justDone, setJustDone] = useState(0);
  const [editing, setEditing] = useState<ScheduleItem>();
  const [savedSchedule, setSavedSchedule] = useState("");

  const load = useCallback(() => {
    setErr("");
    setLoading(true);
    void Promise.all([
      api.get<{ cards: ActionCard[] }>("/api/action-cards?status=pending"),
      api.get<{ stats: MyStats }>("/api/me/stats"),
      // 月视图要整月的格子,一次取够 —— 切个 tab 再发一次请求会让日历闪一下
      api.get<{ schedule: ScheduleData }>("/api/schedule?days=45"),
    ]).then(([c, s, sc]) => {
      setCards(c.cards);
      setStats(s.stats);
      setSched(sc.schedule);
    }).catch((e) => setErr(String((e as Error).message)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load, version]);
  useEffect(() => {
    const timer = setInterval(() => { if (document.visibilityState === "visible") load(); }, 8000);
    return () => clearInterval(timer);
  }, [load]);

  const onResolved = (): void => { setJustDone((n) => n + 1); load(); };

  if (loading && cards.length === 0 && !stats) return <LoadingState label="正在准备行动导航" />;
  if (err) return <ErrorState message={err} onRetry={load} />;

  return (
    <div className="action-navigation">
      <div className="navigation-intro">
        <Greeting name={props.userName} />
        <div className="navigation-status" aria-label="行动概况">
          <a href="#today-actions"><Icon name="sparkles" size={14} />待处理建议 <b>{cards.length}</b> 件</a>
          <a href="#today-followups" className={sched?.overdue ? "has-overdue" : ""}><Icon name="calendar" size={14} />逾期待跟进 <b>{sched?.overdue ?? "—"}</b> 家</a>
          <button className="b sm" disabled={loading} onClick={load}><Icon name="refresh" size={14} />刷新</button>
        </div>
      </div>

      {savedSchedule && <div className="schedule-saved" role="status"><Icon name="check" size={16} /><span>{savedSchedule}</span>
        <button type="button" className="icon-button" aria-label="关闭日程保存提示" onClick={() => setSavedSchedule("")}><Icon name="close" size={15} /></button>
      </div>}

      {stats && <div className="home-grid navigation-overview"><DistributionCard stats={stats} /><RevenueCard stats={stats} /></div>}

      <section className="calendar-section" aria-labelledby="today-calendar-title">{sched && <Suspense fallback={<LoadingState label="正在打开日程" />}><WorkCalendar data={sched} onEdit={setEditing} canViewHistory={props.canViewCustomers !== false} /></Suspense>}</section>

      <div className="work-grid">
        <section id="today-actions" className="work-actions" aria-label="今日行动建议">
          <div className="section-heading"><div><h2><Icon name="sparkles" size={18} />今日行动<span className="tag tBrand">{cards.length} 件</span></h2><p>依据沟通、客户进展及可用知识生成，确认后再行动。</p></div>
            {justDone > 0 && <span className="muted" role="status">已处理 {justDone} 件</span>}
          </div>
          {cards.length === 0 ? <div className="card"><EmptyState title="当前没有待办建议" description="打开一个客户并上传沟通记录。AI 将结合客户沟通与可用知识，准备下一步建议。" icon="sparkles"><button className="b p" onClick={goCustomers}>查看我的客户<Icon name="arrow" size={16} /></button><button className="b" onClick={() => goKnowledge()}>准备知识资料</button></EmptyState></div>
            : cards.map((c) => <ActionCardView key={c.id} card={c} onResolved={onResolved} />)}
        </section>
        <aside id="today-followups" className="work-agenda" aria-label="跟进提醒">
          {sched && sched.recent.length > 0 && <RecentlyContacted data={sched} />}
          {sched && <FollowupAgenda data={sched} onEdit={setEditing} />}
          <div className="card"><div className="panel-heading"><div><h2>一次跟进，三步完成</h2><p>让沟通资料成为可执行的下一步</p></div></div><ol className="workflow-steps"><li>打开客户，查看当前进展</li><li>上传录音、聊天或会议记录</li><li>核对 AI 建议，安排下一次跟进</li></ol><button className="b q panel-link" onClick={goCustomers}>进入我的客户<Icon name="arrow" size={15} /></button></div>
        </aside>
      </div>
      {editing && <ScheduleEditor key={editing.customerId} item={editing} onClose={() => setEditing(undefined)} onSaved={(followup) => {
        setSavedSchedule(`已将「${editing.customerName}」的跟进改到 ${followup.dueOn}`);
        setEditing(undefined); notifyChanges();
      }} />}
    </div>
  );
}

const WEEKDAY = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

function Greeting(props: { name: string }): React.JSX.Element {
  const now = new Date();
  const h = now.getHours();
  // 原型写死「早上好」。真实时间下午还说早上好会显得系统没在看着你
  const hello = h < 6 ? "你好" : h < 12 ? "早上好" : h < 18 ? "下午好" : "晚上好";
  return (
    <header className="work-greeting"><h1>{hello}，{props.name}</h1>
      <p>今天是 {now.getFullYear()} 年 {now.getMonth() + 1} 月 {now.getDate()} 日 · {WEEKDAY[now.getDay()]}</p>
    </header>
  );
}

function FollowupAgenda({ data, onEdit }: { data: ScheduleData; onEdit: (item: ScheduleItem) => void }): React.JSX.Element {
  const [showAll, setShowAll] = useState(false);
  const overdue = data.items.filter((item) => item.overdueDays > 0).sort((a, b) => b.overdueDays - a.overdueDays);
  const upcoming = data.items.filter((item) => item.overdueDays <= 0).sort((a, b) => a.dueOn.localeCompare(b.dueOn));
  const items = overdue.length ? overdue : upcoming;
  return <div className="card"><div className="panel-heading"><div><h2>{overdue.length ? "逾期跟进提醒" : "近期跟进"}</h2><p>{overdue.length ? "按逾期天数排序，点击客户处理" : "按建议跟进日期排序"}</p></div><span className={`tag ${overdue.length ? "tW" : "tN"}`}>{items.length}</span></div>
    {items.length ? items.slice(0, showAll ? items.length : 5).map((item) => <div className="schedule-agenda-row" key={item.customerId}>
      <button className="agenda-item" onClick={() => goCustomer(item.customerId)}><span><b>{item.customerName}</b><small>{item.stageLabel} · {item.dueOn.slice(5)}</small></span><em style={{ color: item.overdueDays > 0 ? undefined : "var(--ink3)" }}>{item.overdueDays > 0 ? `${item.overdueDays} 天` : "待跟进"}</em></button>
      <EditScheduleButton item={item} onEdit={onEdit} />
    </div>) : <EmptyState title="暂无跟进安排" description="在日历中安排客户的下一次跟进。" icon="calendar" />}
    {items.length > 5 && <button className="b q panel-link" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>{showAll ? "收起提醒" : `查看全部 ${items.length} 家`}</button>}
  </div>;
}

/** 刚沟通过多久。系统里只有时间戳,这里只做人话,不编「几点约好的」。 */
function sinceLabel(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return "刚刚";
  if (h < 24) return `${h} 小时前`;
  const d = Math.floor(h / 24);
  return d === 1 ? "昨天" : d === 2 ? "前天" : `${d} 天前`;
}

/**
 * 最近沟通(#63 反馈):「近期跟进」按下次该跟的日期排,刚聊完的客户恰恰排在最后,销售找不到它。
 * 这里按最近沟通时间列出三天内聊过的客户,并标出系统有没有给下一步 —— 没给的那一行就是该去客户页看原因的。
 */
function RecentlyContacted({ data }: { data: ScheduleData }): React.JSX.Element {
  return <div className="card"><div className="panel-heading"><div><h2>最近沟通</h2><p>三天内聊过的客户，最近的在前</p></div><span className="tag tN">{data.recent.length}</span></div>
    {data.recent.map((item) => <div className="schedule-agenda-row" key={item.customerId}>
      <button className="agenda-item" onClick={() => goCustomer(item.customerId)}><span><b>{item.customerName}</b><small>{item.stageLabel} · {sinceLabel(item.lastInteractionAt)}</small></span><em style={{ color: item.hasPendingCard ? undefined : "var(--ink3)" }}>{item.hasPendingCard ? "有建议待处理" : "暂无建议"}</em></button>
    </div>)}
  </div>;
}

/** 📊 我的客户分布 —— A/B/C 三块 + 一行流转状态。 */
function DistributionCard(props: { stats: MyStats }): React.JSX.Element {
  const s = props.stats;
  const n = (g: string): number => s.pyramid.find((p) => p.grade === g)?.count ?? 0;
  return (
    <section className="card tile navigation-panel" aria-labelledby="today-distribution-title">
      <div className="navigation-panel-heading"><h2 id="today-distribution-title"><Icon name="users" size={17} />我的客户分布</h2>
        <button className="navigation-text-link" onClick={goCustomers}>查看客户<Icon name="arrow" size={14} /></button>
      </div>
      <div className="navigation-buckets">
        <Bucket n={n("A")} label="A 类" tone="a" />
        <Bucket n={n("B")} label="B 类" tone="" />
        <Bucket n={n("C")} label="C 类" tone="c" />
      </div>
      <div className="navigation-panel-footer">
        <span>在跟 <b>{s.open}</b> 家</span>
        <span>报价中 <b style={{ color: "var(--amber)" }}>{s.quoting}</b></span>
        <span title={`超过 ${s.coolingDays} 天没有新的沟通记录`}>
          掉温 <b style={{ color: "var(--red)" }}>{s.cooling}</b>
        </span>
        <span style={{ color: "var(--ink3)" }}>已淘汰 {s.dropped}</span>
      </div>
    </section>
  );
}

function Bucket(props: { n: number; label: string; tone: string }): React.JSX.Element {
  return (
    <div className={`bucket ${props.tone}`}>
      <div className="bucket-n">{props.n}</div>
      <div className="bucket-l">{props.label}</div>
    </div>
  );
}

/** 💰 业绩统计 —— 本周/本月/本季度/本年度四段 + 年目标完成率。 */
function RevenueCard(props: { stats: MyStats }): React.JSX.Element {
  const s = props.stats;
  return (
    <section className="card tile navigation-panel" aria-labelledby="today-revenue-title">
      <div className="navigation-panel-heading"><h2 id="today-revenue-title"><Icon name="target" size={17} />业绩统计</h2><span>签约金额</span></div>
      <div className="navigation-buckets navigation-revenue">
        <Bucket2 v={s.revenue.week} label="本周" hot={false} />
        <Bucket2 v={s.revenue.month} label="本月" hot={false} />
        <Bucket2 v={s.revenue.quarter} label="本季度" hot />
        <Bucket2 v={s.revenue.year} label="本年度" hot />
      </div>
      <div className="navigation-panel-footer">
        {/* 没设年目标就整行不显示完成率 —— 不拿一个编出来的目标去算百分比 */}
        {s.annualTarget != null && s.annualPercent != null ? (
          <span>
            年目标 {fmtMoney(s.annualTarget)} ·{" "}
            <b style={{ color: "var(--green)" }}>{s.annualPercent}%</b>
          </span>
        ) : (
          <span style={{ color: "var(--ink3)" }} title="管理者可在「团队与权限」里设定">
            未设定年目标
          </span>
        )}
        <span>
          本月新签 <b>{s.wonThisMonth}</b> · 在谈{" "}
          <b style={{ color: "var(--amber)" }}>{s.negotiating}</b>
        </span>
      </div>
      {s.annualTarget != null && s.annualPercent != null && <div className="navigation-target-progress" role="progressbar" aria-label="年度目标完成率"
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100, Math.max(0, s.annualPercent))} aria-valuetext={`${s.annualPercent}%`}>
        <span style={{ width: `${Math.min(100, Math.max(0, s.annualPercent))}%` }} />
      </div>}
    </section>
  );
}

function Bucket2(props: { v: number; label: string; hot: boolean }): React.JSX.Element {
  return (
    <div className={`bucket ${props.hot ? "g" : ""}`}>
      <div className="bucket-n sm">{fmtMoney(props.v)}</div>
      <div className="bucket-l">{props.label}</div>
    </div>
  );
}
